// src/hooks/useAuth.js
// S27-CHART-ENGINE-hotfix8: Wire apiFetch into auth lifecycle
//
// Change from previous version:
//   Added configureApiFetch + updateApiFetchToken calls so that ALL fetch()
//   calls in the app (not just axios calls) get automatic token refresh.
//   apiFetch.js is the fetch-layer counterpart of the axios interceptor.
//
// Token refresh now covers:
//   axios calls  → axios interceptor (this file, unchanged)
//   fetch() calls → apiFetch (new — wired here via configureApiFetch)
//
// Both share the same refresh endpoint, deduplication pattern, and logout path.

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../styles/shared';
import { configureApiFetch, updateApiFetchToken } from '../utils/apiFetch';

// ─── helpers ─────────────────────────────────────────────────────────────────
function readStorage(key, parse = false) {
  try {
    const val = localStorage.getItem(key);
    if (!val) return null;
    return parse ? JSON.parse(val) : val;
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Module-level token store
//
// The 401 interceptor runs outside React's render cycle — it needs the current
// access token without a hook call. _accessToken mirrors the React state and is
// updated whenever loginSuccess / logout / the refresh interceptor fires.
//
// _refreshInFlight: if multiple requests 401 at once, only one /refresh call is
// made. All waiters share the same promise and retry with the same new token.
// ─────────────────────────────────────────────────────────────────────────────
let _accessToken    = null;
let _refreshInFlight = null;
let _logoutCallback  = null; // wired by useAuth — called when refresh fails
let _tokenCallback   = null; // wired by useAuth — updates React state on refresh

// Install interceptors once, even if multiple components use useAuth()
let _interceptorsInstalled = false;

function installInterceptors() {
  if (_interceptorsInstalled) return;
  _interceptorsInstalled = true;

  // Attach access token to every outgoing axios request automatically
  axios.interceptors.request.use(config => {
    if (_accessToken && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${_accessToken}`;
    }
    return config;
  });

  // axios 401 → attempt silent refresh → retry original request
  axios.interceptors.response.use(
    res => res,
    async error => {
      const original = error.config;

      // Only handle 401 — and only once per request (prevent infinite retry)
      if (error.response?.status !== 401 || original._refreshRetry) {
        return Promise.reject(error);
      }

      // If the failing request WAS the refresh call itself → token is dead → logout
      if (original.url?.includes('/api/auth/refresh')) {
        _logoutCallback?.();
        return Promise.reject(error);
      }

      original._refreshRetry = true;

      // Deduplicate concurrent 401s — only one refresh call in flight at a time
      if (!_refreshInFlight) {
        _refreshInFlight = axios
          .post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true })
          .then(res => {
            const newToken = res.data.token;
            _accessToken = newToken;
            _tokenCallback?.(newToken); // update React state + apiFetch
            return newToken;
          })
          .catch(err => {
            _logoutCallback?.(); // refresh failed — log user out cleanly
            throw err;
          })
          .finally(() => { _refreshInFlight = null; });
      }

      try {
        const newToken = await _refreshInFlight;
        original.headers = { ...original.headers, Authorization: `Bearer ${newToken}` };
        return axios(original); // retry original request with fresh token
      } catch {
        return Promise.reject(error);
      }
    }
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// useAuth
//
// FIX (S27E-hotfix2): Token initialised synchronously via lazy useState.
// auth-refresh-tokens session: adds 401 interceptor + /refresh + /logout calls.
// S27-CHART-ENGINE-hotfix8: Wires apiFetch so fetch() calls also auto-refresh.
// ─────────────────────────────────────────────────────────────────────────────
export function useAuth() {
  // Lazy initializers run synchronously once before first render
  const [token, setToken] = useState(() => {
    const t = readStorage('marketpulze_token');
    _accessToken = t; // sync module-level token on first mount
    return t;
  });
  const [user, setUser] = useState(() => readStorage('marketpulze_user', true));

  // Wire up axios interceptors + apiFetch + module-level callbacks on mount
  useEffect(() => {
    installInterceptors();

    // Wire apiFetch with the current token and auth callbacks
    // apiFetch covers all raw fetch() calls (PriceChart, D3 signal logs, drawings, etc)
    configureApiFetch({
      token:     _accessToken,
      onRefresh: (newToken) => {
        setToken(newToken);
        localStorage.setItem('marketpulze_token', newToken);
      },
      onLogout: () => logout(),
    });

    // Update React state when the axios interceptor silently refreshes a token
    _tokenCallback = (newToken) => {
      setToken(newToken);
      localStorage.setItem('marketpulze_token', newToken);
      updateApiFetchToken(newToken); // keep apiFetch in sync when axios refreshes
    };

    // The axios interceptor calls this if /refresh itself returns 401
    _logoutCallback = () => logout();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for global logout events (e.g. other tabs, manual dispatch)
  useEffect(() => {
    const handler = () => logout();
    window.addEventListener('logout', handler);
    return () => window.removeEventListener('logout', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loginSuccess = useCallback((newToken, newUser) => {
    _accessToken = newToken;          // keep module-level in sync
    updateApiFetchToken(newToken);    // keep apiFetch in sync
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('marketpulze_token', newToken);
    localStorage.setItem('marketpulze_user', JSON.stringify(newUser));
  }, []);

  const logout = useCallback(async () => {
    // Call server to delete refresh token from DB + clear httpOnly cookie
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch { /* non-critical — clear local state regardless */ }

    _accessToken = null;
    updateApiFetchToken(null);        // clear apiFetch token on logout
    setUser(null);
    setToken(null);
    localStorage.removeItem('marketpulze_token');
    localStorage.removeItem('marketpulze_user');
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      if (res.data.success) {
        loginSuccess(res.data.token, res.data.user);
        return { success: true };
      }
      return { success: false, message: res.data.message };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || 'Login failed' };
    }
  }, [loginSuccess]);

  const register = useCallback(async (email, password) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/auth/register`,
        { email, password },
        { withCredentials: true }
      );
      if (res.data.success) {
        loginSuccess(res.data.token, res.data.user);
        return { success: true };
      }
      return { success: false, message: res.data.message };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || 'Registration failed' };
    }
  }, [loginSuccess]);

  const forgotPassword = useCallback(async (email) => {
    await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
  }, []);

  return { user, token, login, register, logout, loginSuccess, forgotPassword, setUser };
}
